// ─── Composed Theme Presets ───────────────────────────────────
// Each theme combines palette + fontPair + styling tokens.
// Templates reference theme IDs.

import { palettes } from './palettes';
import { fontPairs } from './typography';
import { radius, shadows, overlays, sectionSpacing } from './spacing';

export const themes = {
  'elegant-classic': {
    id: 'elegant-classic',
    name: 'Elegant Classic',
    palette: 'ivory-gold',
    fontPair: 'playfair-inter',
    radius: 'soft',
    shadow: 'soft',
    overlay: 'warm-soft',
    sectionSpacing: { mobileY: '4rem', desktopY: '6rem' },
  },
  'romantic-soft': {
    id: 'romantic-soft',
    name: 'Romantic Soft',
    palette: 'rose-beige',
    fontPair: 'cormorant-manrope',
    radius: 'soft',
    shadow: 'none',
    overlay: 'warm-soft',
    sectionSpacing: { mobileY: '4rem', desktopY: '6rem' },
  },
  'natural-editorial': {
    id: 'natural-editorial',
    name: 'Natural Editorial',
    palette: 'forest-cream',
    fontPair: 'dmserif-plusjakarta',
    radius: 'soft',
    shadow: 'soft',
    overlay: 'none',
    sectionSpacing: { mobileY: '4rem', desktopY: '6rem' },
  },
  'evening-luxury': {
    id: 'evening-luxury',
    name: 'Evening Luxury',
    palette: 'black-champagne',
    fontPair: 'playfair-inter',
    radius: 'soft',
    shadow: 'card',
    overlay: 'dark-soft',
    sectionSpacing: { mobileY: '4rem', desktopY: '6rem' },
  },
};

/**
 * Resolve a theme config into concrete token values.
 * @param {object} themeConfig - { palette, fontPair, radius, shadow, overlay }
 * @param {object} [overrides] - partial overrides from invitation instance
 * @returns {object} Fully resolved theme with all token values
 */
export function resolveTheme(themeConfig, overrides = {}) {
  const merged = { ...themeConfig, ...overrides };
  const basePalette = palettes[merged.palette] || palettes['ivory-gold'];
  const palette = { ...basePalette, ...(merged.colorOverrides || {}) };
  const fonts = fontPairs[merged.fontPair] || fontPairs['playfair-inter'];
  const r = radius[merged.radius] || radius.soft;
  const s = shadows[merged.shadow] || shadows.none;
  const o = overlays[merged.overlay] || overlays.none;
  const sp = merged.sectionSpacing || sectionSpacing;

  return {
    colors: palette,
    fonts,
    radius: r,
    shadow: s,
    overlay: o,
    sectionSpacing: sp,
    // Convenience accessors
    bg: palette.bg,
    surface: palette.surface,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    accent: palette.accent,
    accentSoft: palette.accentSoft,
    border: palette.border,
    buttonText: palette.buttonText,
    buttonBg: palette.buttonBg,
    headingFont: fonts.heading,
    bodyFont: fonts.body,
    eyebrowFont: fonts.eyebrow,
    // Merge any template-specific extensions (used by collage templates)
    ...(merged.themeExtensions || {}),
  };
}
